import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { getSystemUserByEmail, getStaffRole } from '@/services/api';
import type { SystemUser } from '@/types';

interface CurrentUserState {
  // Azure AD account info
  azureEmail: string | undefined;
  azureName: string | undefined;

  // Dataverse system user info
  systemUser: SystemUser | null;

  // Loading/error states
  isLoading: boolean;
  error: string | null;

  // Helper to get preparer ID for API calls
  preparerId: string | undefined;
  preparerName: string | undefined;

  // Role-based access
  isAdmin: boolean;
}

const STORAGE_KEY = 'ptc_current_user';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface CachedUser {
  email: string;
  systemUser: SystemUser;
  timestamp: number;
}

export function useCurrentUser(): CurrentUserState & { refresh: () => Promise<void> } {
  const { accounts } = useMsal();
  const account = accounts[0];

  const [systemUser, setSystemUser] = useState<SystemUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cosmosRoles, setCosmosRoles] = useState<string[]>([]);

  // Extract claims from the ID token
  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
  const tokenSystemUserId = claims?.['extension_attribute1'] as string | undefined;
  const claimRoles = claims?.['roles'] as string[] | undefined;
  const tokenRoles = useMemo(() => claimRoles || [], [claimRoles]);

  const loadSystemUser = useCallback(async (email: string) => {
    // Path 1: If extension_attribute1 is present in the token, use it directly
    if (tokenSystemUserId) {
      const user: SystemUser = {
        systemUserId: tokenSystemUserId,
        fullName: account?.name || email,
        email,
        jobTitle: undefined,
        isDisabled: false,
        roles: tokenRoles,
      };
      setSystemUser(user);
      setIsLoading(false);
      return;
    }

    // Path 2: Fallback to API call (user not yet provisioned with extension attribute)
    console.warn(
      `[useCurrentUser] No extension_attribute1 claim for ${email}. ` +
      'Falling back to /system-users/by-email API call. ' +
      'Set the extensionAttribute1 in Azure AD to the Dataverse systemUserId to avoid this.'
    );

    // Check sessionStorage cache (only used for API fallback path)
    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed: CachedUser = JSON.parse(cached);
        if (
          parsed.email === email &&
          Date.now() - parsed.timestamp < CACHE_DURATION
        ) {
          setSystemUser(parsed.systemUser);
          setIsLoading(false);
          return;
        }
      }
    } catch {
      // Ignore cache errors
    }

    // Fetch from API
    setIsLoading(true);
    setError(null);

    try {
      const user = await getSystemUserByEmail(email);

      if (user) {
        setSystemUser(user);

        // Cache the result
        const cacheData: CachedUser = {
          email,
          systemUser: user,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cacheData));
      } else {
        setError(`No Dataverse user found for ${email}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setIsLoading(false);
    }
  }, [tokenSystemUserId, tokenRoles, account?.name]);

  const refresh = useCallback(async () => {
    if (account?.username) {
      // Clear cache and reload
      sessionStorage.removeItem(STORAGE_KEY);
      await loadSystemUser(account.username);
    }
  }, [account?.username, loadSystemUser]);

  useEffect(() => {
    if (account?.username) {
      loadSystemUser(account.username);
    } else {
      setIsLoading(false);
    }
  }, [account?.username, loadSystemUser]);

  // Fetch Cosmos staff role to check for admin role assignment
  useEffect(() => {
    const userId = systemUser?.systemUserId || tokenSystemUserId;
    if (!userId) return;

    getStaffRole(userId).then(role => {
      if (role?.roles) {
        setCosmosRoles(role.roles);
      }
    }).catch(() => {
      // Ignore - fall back to other admin checks
    });
  }, [systemUser?.systemUserId, tokenSystemUserId]);

  // Compute isAdmin: token roles > Cosmos staff role > jobTitle fallback
  const isAdmin = tokenRoles.includes('Admin') ||
    cosmosRoles.includes('admin') ||
    (!tokenRoles.length && (
      systemUser?.jobTitle?.toLowerCase().includes('admin') ||
      systemUser?.jobTitle?.toLowerCase().includes('manager') ||
      systemUser?.jobTitle?.toLowerCase().includes('owner') || false
    ));

  return {
    azureEmail: account?.username,
    azureName: account?.name,
    systemUser,
    isLoading,
    error,
    preparerId: systemUser?.systemUserId,
    preparerName: systemUser?.fullName,
    isAdmin,
    refresh,
  };
}

export default useCurrentUser;
